import Head from 'next/head'
import Script from 'next/script'
import { FaMoneyBillWave, FaCheckCircle, FaUniversity, FaFileInvoiceDollar, FaListAlt, FaPlusSquare, FaCreditCard, FaBan } from 'react-icons/fa'
import { LegalPageLayout, LegalSection, LegalHighlight } from '../../components/index/LegalPageLayout'

export default function PaymentToServiceProvider() {
  return (
    <>
      <Head>
        <title>Terms on Payment to Service Provider | StringsSG</title>
        <meta charSet="utf-8"/>
        <meta httpEquiv="X-UA-Compatible" content="IE=edge"/>
        <meta name="viewport" content="width=device-width, initial-scale=1, minimum-scale=1.0, maximum-scale=1.0, user-scalable=no"/>
        <meta name="description" content="These terms apply to service partners who directly accepts jobs through StringsSG platform."/>
        <meta name="author" content="StringsSG"/>
        <meta name="google-site-verification" content="JAjj5vAc8Xxrh4idRAchZhYR6rwxWPAk3vxMdx-dwVQ" />
        <meta property="og:url" content="https://www.stringssg.com/terms/payment-to-service-provider" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Terms on Payment to Service Provider | StringsSG"/>
        <meta property="og:description" content="These terms apply to service partners who directly accepts jobs through StringsSG platform." />
        <meta property="og:image" content="https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/Google-icon.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/Google-icon.png" />
        <meta name="twitter:site" content="@stringssg"/> 
        <meta name="twitter:url" content="https://www.stringssg.com/terms/payment-to-service-provider"/>
        <meta name="twitter:title" content="Terms on Payment to Service Provider | StringsSG"/> 
        <meta name="twitter:description" content="These terms apply to service partners who directly accepts jobs through StringsSG platform."/>
        <link rel="icon" href="/favicon.ico" />
        <link rel="canonical" href="https://www.stringssg.com/terms/payment-to-service-provider"/>
      </Head>
      
      <LegalPageLayout
        title="Payment to Service Provider"
        subtitle="Terms on Payment to Service Provider"
        icon={FaMoneyBillWave}
        iconBgColor="from-green-500 to-emerald-600"
        heroBgColor="from-slate-900 via-emerald-900 to-green-900"
      >
        <LegalHighlight color="green">
          <p>
            Please find below the terms for payment from StringsSG to service partners, including the requirement for adherence to invoicing guidelines.
          </p>
        </LegalHighlight>

        <div className="bg-white rounded-xl p-6 border border-slate-200 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <FaMoneyBillWave className="text-green-600" />
            </div>
            <h2 className="text-lg font-bold text-slate-900">1. Payment Terms</h2>
          </div>
          <p className="text-slate-600">
            Payment from StringsSG to service provider&apos;s Stripe payout account will automatically occur once the client has made a payment. StringsSG funnels direct transfer of the due amount
            from the client to the service provider&apos;s Payout account setup on the StringsSG platform.
          </p>
        </div>

        <div className="bg-white rounded-xl p-6 border border-slate-200 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <FaCheckCircle className="text-blue-600" />
            </div>
            <h2 className="text-lg font-bold text-slate-900">2. Payment Confirmation</h2>
          </div>
          <p className="text-slate-600">
            Upon completion of the service, the service provider must ensure that the client has made the full payment for the job. 
            Service provider must verify the payment status on the StringsSG platform to confirm that the payment has been made in full.
            Failure to confirm payment after the service may delay the credit of earnings to the service provider&apos;s account and sometimes
            result in non-payment if StringsSG is unable to collect the payment from the client/service requester.
          </p>
        </div>

        <div className="bg-white rounded-xl p-6 border border-slate-200 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <FaUniversity className="text-purple-600" />
            </div>
            <h2 className="text-lg font-bold text-slate-900">3. Payment to Service Provider&apos;s Bank Account</h2>
          </div>
          <p className="text-slate-600">
            Service provider can transfer the available balance from their payout account to their bank account or debit card depending on how they are setup on the platform.
          </p>
        </div>

        <div className="bg-white rounded-xl p-6 border border-slate-200 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <FaFileInvoiceDollar className="text-amber-600" />
            </div>
            <h2 className="text-lg font-bold text-slate-900">4. Valid Invoice</h2>
          </div>
          <p className="text-slate-600">
            A valid invoice must include all necessary information as outlined in the invoicing guidelines provided by StringsSG. The service provider is responsible for generating a 
            correct and complete invoice in a timeline manner.
          </p>
        </div>

        <div className="bg-white rounded-xl p-6 border border-slate-200 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
              <FaListAlt className="text-orange-600" />
            </div>
            <h2 className="text-lg font-bold text-slate-900">5. Invoicing Guidelines</h2>
          </div>
          <div className="text-slate-600 space-y-4">
            <p>
              The service provider must adhere to the invoicing guidelines provided by StringsSG. Any deviation from the guidelines may result in delay or rejection of payment from client.
            </p>
            <div className="bg-slate-50 rounded-lg p-4">
              <ol className="list-decimal pl-4 space-y-2 text-sm">
                <li>Additional costs to the client must be confirmed by the service provider with client before the invoice is updated.</li>
                <li>StringsSG handles invoicing to the client. Service partners must not issue any other invoice to the client from their side.</li>
                <li>Service partners must follow any instructions related to invoicing, issued over email to their registered email Id.</li>
              </ol>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 border border-slate-200 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
              <FaPlusSquare className="text-indigo-600" />
            </div>
            <h2 className="text-lg font-bold text-slate-900">6. Approval for Additional Work</h2>
          </div>
          <div className="text-slate-600 space-y-4">
            <p>
              In the event that additional work is required beyond the scope of the original request, service partners are required to use the &quot;Request New Job&quot; feature to 
              get confirmation from the client.
            </p>
            <div className="space-y-3">
              <div className="bg-indigo-50 rounded-lg p-4">
                <p className="font-semibold text-indigo-900 mb-2">(i) Request New Job:</p>
                <p className="text-sm">Service partners may submit a new request on behalf of the client with quotation for the additional work on the platform 
                and await confirmation from the account holder before proceeding with the extra work.</p>
              </div>
              <div className="bg-indigo-50 rounded-lg p-4">
                <p className="font-semibold text-indigo-900 mb-2">(ii) Chat Approval:</p>
                <p className="text-sm">Alternatively, service partners may use the StringsSG platform&apos;s chat feature to discuss and obtain approval for the additional costs 
                associated with the extra work. Work should not commence until the account holder has provided written approval through the chat.</p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 border border-slate-200 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
              <FaCreditCard className="text-teal-600" />
            </div>
            <h2 className="text-lg font-bold text-slate-900">7. Payment Mode</h2>
          </div>
          <p className="text-slate-600">
            StringsSG funnels direct transfer of the due amount from the client to the service provider&apos;s Payout account setup on the StringsSG platform.
            In some cases, payment shall be made via the mode specified by StringsSG, such as bank transfer or online payment.
          </p>
        </div>

        <div className="bg-red-50 border border-red-200 rounded-xl p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
              <FaBan className="text-red-600" />
            </div>
            <h2 className="text-lg font-bold text-slate-900">8. Termination of Services</h2>
          </div>
          <p className="text-red-700">
            StringsSG reserves the right to terminate services in the event of a breach of agreement or non-compliance with invoicing guidelines.
          </p>
        </div>

        <div className="bg-slate-100 rounded-xl p-6">
          <p className="text-slate-700">
            By agreeing to these terms and conditions, the service provider acknowledges their responsibility to generate accurate and complete invoices in 
            compliance with invoicing guidelines provided by StringsSG. Payment shall be made only for valid invoices generated in accordance with the guidelines.
          </p>
        </div>
      </LegalPageLayout>

      <Script 
        id="google-tagmanager-new"
        src="https://www.googletagmanager.com/gtag/js?id=G-QD1HGGHDYJ"
        strategy="lazyOnload"
        onLoad={() => {              
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', 'G-QD1HGGHDYJ');
        }} 
      />
      <Script id="facebook-tracking" strategy="lazyOnload">
        {`!function(f,b,e,v,n,t,s)
          {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
          n.callMethod.apply(n,arguments):n.queue.push(arguments)};
          if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
          n.queue=[];t=b.createElement(e);t.async=!0;
          t.src=v;s=b.getElementsByTagName(e)[0];
          s.parentNode.insertBefore(t,s)}(window, document,'script',
          'https://connect.facebook.net/en_US/fbevents.js');
          fbq('init', '128634041116395');
          fbq('track', 'PageView');`}
      </Script>
    </>
  )
}
